/**
 * Centralised analytics helpers for the Comment RPA modal.
 *
 * Every BigQuery event shares a common envelope; individual methods
 * add the event-specific `event_name` and `additional_data` fields.
 *
 * @module commentRPAnewModal/hooks/useCommentRpaAnalytics
 */
import { useSelector } from "react-redux";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";

/**
 * Provides named analytics methods for BO dropdown interactions inside the
 * Comment RPA modal.
 *
 * @param orderId - Resolved order ID used as both `patient_id` and `order_id`
 * @param patinetId - Patient identifier for analytics envelope
 * @returns Object with dropdown analytics-firing methods
 */
export const useCommentRpaAnalytics = (orderId: string, patinetId: string) => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const orgId = getOrgIdForFetchExternalWorklist();

  const fireEvent = (
    eventName: string,
    extra: Record<string, unknown> = {},
  ) => {
    apiWorkerManager.enqueueAndProcess({
      patient_id: patinetId,
      order_id: orderId,
      org_id: orgId,
      event_name: eventName,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        modal_name: ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
        payer_name: payerName,
        ...extra,
      },
    });
  };

  /** Fires when the BO status dropdown is opened. */
  const logDropdownClicked = (defaultValue: string) => {
    fireEvent(EventsName.COMMENT_RPA_MODAL_DROPDOWN_CLICKED, {
      default_value: defaultValue,
    });
  };

  /** Fires when a BO status option is selected from the dropdown. */
  const logDropdownSelected = (defaultValue: string, selectedValue: string) => {
    fireEvent(EventsName.COMMENT_RPA_MODAL_DROPDOWN_SELECTED, {
      default_value: defaultValue,
      selected_value: selectedValue,
    });
  };

  return {
    logDropdownClicked,
    logDropdownSelected,
  };
};
